import React, { Suspense } from 'react'
import { filter } from 'lodash'
import { isBrowser, isTablet } from 'react-device-detect'
import { colors } from '@lmc/common/constants'
import { getNominative } from '../../common/utils'
import { gaEvent } from '../../common/services/analytics'
import CarePlanPdfExport from '../pdf/CarePlanPdfExport'
import LastUpdateBox from './LastUpdateBox'
import ResidentQuoteBox from './ResidentQuoteBox'
import { ErrorBox } from '../generic'
import Field from '../fields'
import { Button, IconType, Text, Icon } from '@lmc/ui-kit'
import { CarePlan, Carer } from '@lmc/ts-common'
import styled from 'styled-components'

interface CarePlanSummaryHeaderProps {
  carePlan: CarePlan
  carer?: Carer | null
  errors: any[] // Update the type to match your error shape
  fields: any // Update the type to match your fields shape
  formId: string | null
  setFormField: (params: { key: string; value: any }) => void
  onClickBack: () => void
  onClickHistory: () => void
  rightSection?: () => React.ReactNode
  resident: {
    name: {
      first: string
      last: string
    }
  } | null
  isInitialAssessment: boolean
  logs?: any
  carePlanNominative?: any
}

class CarePlanSummaryHeader extends React.Component<
  any,
  { editTitle: boolean }
> {
  state = {
    editTitle: false
  }

  onClickHistory = () => {
    const { carePlan, onClickHistory } = this.props

    onClickHistory()
    gaEvent({
      category: 'CarePlans',
      action: `Open History`,
      label: carePlan.carePlanAdl.name
    })
  }

  renderInnerTitle = () => {
    const {
      carePlan,
      carer,
      // @ts-ignore
      logs,
      formId,
      resident,
      // @ts-ignore
      carePlanNominative,
      rightSection
    } = this.props

    if (!resident) {
      return typeof rightSection === 'function' ? rightSection() : null
    }

    const pdfTitle = `${resident.name.first} ${
      resident.name.last
    } - ${getNominative(carePlanNominative, 'Care Plan')} - ${
      carePlan.carePlanAdl.name
    }`
    return (
      <ButtonRow>
        {!carePlan.isRevision && (
          <Button
            disabled={!!formId}
            // @ts-ignore
            id="care-plan-history-button"
            dataTestId="care-plan-history"
            onClick={() => this.onClickHistory()}
            style={{ marginRight: 10 }}
            variant="default"
            // @ts-ignore
            icon={<Icon name="HistoryOutlined"></Icon>}
          >
            History
          </Button>
        )}
        {isDesktop && (
          <CarePlanPdfExport
            carePlan={carePlan}
            carePlanNominative={carePlanNominative}
            carer={carer}
            logs={filter(logs, { deleted: false })}
            disabled={!!formId}
            // @ts-ignore
            id="care-plan-pdf-download-button"
            resident={resident}
            title={pdfTitle}
            reviewComment={carePlan.reviewComment}
          />
        )}
      </ButtonRow>
    )
  }

  renderHeader = () => {
    const { carePlan, onClickBack, resident } = this.props

    const backText = carePlan.isRevision ? 'History' : 'Back'

    if (!resident) {
      return (
        <HeaderContainer>
          <a onClick={carePlan.isRevision ? this.onClickHistory : onClickBack}>
            <span className="octicon octicon-chevron-left" /> {backText}
          </a>
        </HeaderContainer>
      )
    }

    return (
      <HeaderContainer>
        <a onClick={carePlan.isRevision ? this.onClickHistory : onClickBack}>
          <span className="octicon octicon-chevron-left" /> {backText}
        </a>
      </HeaderContainer>
    )
  }

  onEditTitle = () => {
    const { carePlan, setFormField } = this.props

    this.setState({ editTitle: true })
    setFormField({
      key: 'customName',
      value: carePlan.customName || carePlan.carePlanAdl.name
    })
  }

  renderTitleField = () => {
    const { fields, carePlan, formId, isInitialAssessment } = this.props
    return this.state.editTitle ? (
      <Suspense fallback={<div>Loading...</div>}>
        <TitleInput
          className="lmc-sub-card-edit"
          keys="customName"
          layout="no-label"
          formId={formId}
          type="text"
          values={fields}
        />
      </Suspense>
    ) : (
      <AdlName id="adl-name">
        {carePlan.customName || carePlan.carePlanAdl?.name}
        {!isInitialAssessment && formId && (
          <StyledButton
            className="lmc-sub-card-edit"
            onClick={this.onEditTitle}
          >
            <Icon customIcon={IconType.Pencil} />
          </StyledButton>
        )}
      </AdlName>
    )
  }

  renderTitle = () => {
    const { carePlan } = this.props
    const adl = carePlan.carePlanAdl

    return (
      <TitleContainer>
        <IconContainer background={adl?.color || colors.darkgrey}>
          <Image src={adl?.icon} alt="" />
        </IconContainer>
        <AdlNameContainer>{this.renderTitleField()}</AdlNameContainer>
        {this.renderInnerTitle()}
      </TitleContainer>
    )
  }

  renderLastReview = (carePlan: any, carer: Carer | null) => {
    const { isInitialAssessment } = this.props
    if (carePlan.editedOn && carePlan.editedBy && !carer) return null

    const carePlanTitle = carePlan.isRevision ? 'Reviewed on' : 'Last review'
    const title = isInitialAssessment ? 'Last update' : carePlanTitle
    return (
      <LastUpdateBox
        carer={carer}
        date={carePlan.editedOn}
        noDateText="No reviews yet"
        title={title}
        comment={carePlan.reviewComment}
      />
    )
  }

  renderOutcomes = () => {
    const { carePlan, fields, formId, resident } = this.props

    if (!resident) return null

    const fieldProps = formId && {
      className: 'lmc-sub-card-edit',
      formId,
      keys: 'outcomes',
      label: 'Outcomes',
      layout: 'no-label',
      type: 'textarea',
      values: fields
    }
    return (
      <div style={{ paddingTop: 48 }}>
        <OutcomesTitleWrapper>
          <Text as="h4">Desired outcomes</Text>
        </OutcomesTitleWrapper>
        <QuoteBox
          fieldProps={fieldProps}
          resident={resident}
          text={carePlan.outcomes || carePlan.carePlanAdl.outcomes}
        />
      </div>
    )
  }

  render() {
    const { carePlan, carer, errors, resident, formId } = this.props
    if (!carePlan) return null
    return (
      <div>
        {this.renderHeader()}
        {this.renderTitle()}
        <Errors errors={errors} />
        {this.renderLastReview(carePlan, carer as Carer)}
        {this.renderOutcomes()}
      </div>
    )
  }
}

const isDesktop = isBrowser || isTablet

const StyledButton = styled(Button)`
  background: transparent !important;
  position: relative;
  top: -1px;
  height: 30px;
  color: ${colors.black} !important;
  margin-left: 6px;
  transition: all 0.2s ease;
  &:hover {
    background: ${colors.orange} !important;
    color: white !important;
  }
`

const ButtonRow = styled.div`
  display: flex;
`

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
`

const TitleInput = styled(Field)`
  padding-bottom: 3px;
  font-size: 2em;
  font-weight: 400;
`

const AdlName = styled.h2`
  margin-bottom: 0;
  min-height: 34px;
`

interface IconContainerProps {
  background: string
}

const IconContainer = styled.div<IconContainerProps>`
  border-radius: 4px;
  text-align: center;
  display: inline-block;
  vertical-align: top;
  height: 40px;
  width: 40px;
  background: ${(props) => props.background};
`

const Image = styled.img`
  color: white;
  height: 20px;
  margin-top: 10px;
`

const AdlNameContainer = styled.div`
  flex: 1;
  padding-left: 15px;
  font-weight: 300;
`

const TitleContainer = styled.div`
  display: flex;
  padding-top: 21px;
  margin-bottom: 12px;
  margin-top: 12px;
  background: ${colors.white};
  padding: 18px 22px;
  border-radius: 15px;
  align-items: center;
  justify-content: space-between;
`

const Errors = styled(ErrorBox)`
  margin-bottom: 25px;
`

const QuoteBox = styled(ResidentQuoteBox)`
  margin-top: 12px;
`

QuoteBox.displayName = 'QuoteBox'

const OutcomesTitleWrapper = styled.div`
  color: ${colors.black};
  opacity: 0.7;
`

export default CarePlanSummaryHeader
